import { useCallback, useState } from 'react';
import { useHandleApiResponse, useToast } from '../../../../hooks';
import { useGenerateUploadUrlMutation, useUploadFileMutation } from '../../../../services';
import { FilePurpose, GenerateUploadUrlResponseData, ProspectCategory, ProspectType } from '../../../../types';
import { AlertType, ButtonGroup, FileInput, TextButton } from '../../../shared';

const UPLOAD_FILE_ERROR_MSG = 'Failed to upload file. Please try again later.';

interface UploadMediaFileProps {
  onSuccess: () => void;
  onBack: () => void;
  newCategory?: ProspectCategory;
  newType?: ProspectType;
}

const UploadMediaFile = ({ onBack, onSuccess, newCategory, newType }: UploadMediaFileProps) => {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [error, setError] = useState<string>('');

  const [generateUploadUrl, { isLoading: isGeneratingUploadUrl }] = useGenerateUploadUrlMutation();
  const [uploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const handleUploadFile = useCallback(
    async (data: GenerateUploadUrlResponseData) => {
      if (!data?.length || !file) return;
      try {
        const uploadUrl = data[0].presignedUrl;
        const response = await uploadFile({ file, uploadUrl });
        if (response.error) {
          showToast({ message: UPLOAD_FILE_ERROR_MSG, type: AlertType.ERROR });
        } else {
          onSuccess();
        }
      } catch (error) {
        console.error(error);
        showToast({ message: UPLOAD_FILE_ERROR_MSG, type: AlertType.ERROR });
      }
    },
    [file, onSuccess, showToast, uploadFile]
  );
  const handleGenerateUploadUrl = useCallback(async () => {
    if (!file || !newCategory || !newType) return;

    try {
      // Generate upload URL
      const response = await generateUploadUrl({
        files: [
          {
            name: file.name,
            type: file.type,
            size: file.size,
            purpose: FilePurpose.PROSPECT_CREATION,
            prospectCategory: newCategory,
            prospectType: newType,
          },
        ],
      });

      handleApiResponse({
        response,
        errorMsg: UPLOAD_FILE_ERROR_MSG,
        hideSuccessToast: true, // Hide the success toast because succeeding starts the upload process right away.
        onSuccess: handleUploadFile, // Upload file after generating upload URL
      });
    } catch (error) {
      console.error(error);
      showToast({ message: UPLOAD_FILE_ERROR_MSG, type: AlertType.ERROR });
    }
  }, [file, generateUploadUrl, handleApiResponse, handleUploadFile, newCategory, newType, showToast]);

  return (
    <div className="flex flex-col gap-4">
      <FileInput setSelectedFile={setFile} selectedFile={file} setError={setError} error={error} />
      <ButtonGroup>
        <TextButton
          onClick={handleGenerateUploadUrl}
          text="Next"
          loading={isGeneratingUploadUrl || isUploadingFile}
          disabled={!file}
        />
        <TextButton onClick={onBack} text="Back" />
      </ButtonGroup>
    </div>
  );
};

export default UploadMediaFile;
